// @erp/module-lib/fe · scan/bridge — the SCAN-4 TRANSPORT half of the phone
// scanner: the desk's relay subscription and the phone's pairing/publish calls.
// Ported from shippra `apps/libms/src/lib/scan/scanBridge.ts` with the
// divergences modules/lib/decisions.md pins:
//
//   • SESSION — `makeScanSession()` (crypto.randomUUID) minted per PAIRING ACT,
//     NOT the source's `deriveSession = SHA-256(userId)`. That made the relay
//     user-scoped and its id deterministic, replayable and shared across every
//     desk of an account; ADR-003 §6 says ScanRelay is pairing-session-scoped.
//     The source's Math.random fallbacks in makeSession/makeAuthCode are dropped
//     with it — the minters live in the shared contract and use Web Crypto only.
//
//   • RELAY ORIGIN — same-origin `/scan/*`, NOT the source's
//     `VITE_API_URL.replace(/\/api\/v1$/,'')` string-munge. Every portal already
//     proxies the gateway (the module client is based at `/api/lib`), so the
//     relay is reachable at the portal's own origin and the desk's cookie stays
//     first-party. Dev proxies `/scan` with `ws: true`.
//
//   • DESK SOCKET — the session id rides `Sec-WebSocket-Protocol`, never `?s=`
//     (SCAN-3 gate (b)); the desk's ops are cookie-authenticated (gate (a)), so
//     `registerHost` sends credentials while the phone's calls never do.
import {
  SCAN_WS_PROTOCOL,
  type DeviceInfo,
  type ScanKind,
} from "../../schemas/scan.js";

/** The query key the pairing QR puts the session id under, on the PHONE's page URL. */
const SESSION_KEY = "s";
/**
 * The phone PAGE path. Deliberately NOT `/scan` (the source's choice): the relay
 * API owns `/scan/*` on this origin, and a portal proxying that prefix to the
 * gateway would swallow the page too. The source avoided the clash only because
 * its API lived on a separate origin.
 */
export const PHONE_SCAN_PATH = "/scanner";
const PHONE_TOKEN_PREFIX = "erp.scanlink.phone.";

/** A decoded scan as the desk consumes it. */
export interface ScanEvent {
  kind: ScanKind;
  value: string;
  raw: string;
  format?: string;
  t: number;
}

/** 'offline' = the relay accepted it but no desk page was listening, so it was DROPPED. */
export type SendResult = "ok" | "offline" | "unpaired" | "error";

/* ─────────────────────────────── URLs + storage ─────────────────────────────── */

/** Hosts that mean "this machine" — a phone scanning one of these would call
 * ITSELF, so the pairing QR must never carry one. */
const LOOPBACK = ["localhost", "127.0.0.1", "::1", "[::1]", "0.0.0.0"];
export const isLoopbackHost = (host = window.location.hostname): boolean => LOOPBACK.includes(host);

/** The dev server's LAN IPv4, injected by the shared Vite preset; "" in prod. */
declare const __DEV_LAN_HOST__: string | undefined;
const devLanHost = (): string => {
  try {
    return typeof __DEV_LAN_HOST__ === "string" ? __DEV_LAN_HOST__ : "";
  } catch {
    return ""; // not defined (tests, non-Vite bundlers)
  }
};

/**
 * The origin the pairing QR should carry. Normally the desk's own origin — but
 * a console opened on http://localhost would encode a QR that resolves to the
 * PHONE when scanned and can never reach the desk. In DEV the Vite preset hands
 * over the machine's LAN IPv4 (__DEV_LAN_HOST__), so the loopback host is
 * swapped for it, port and protocol kept. In production the origin is already a
 * real host and nothing is substituted.
 */
export const pairingOrigin = (): string => {
  const { origin, hostname, port, protocol } = window.location;
  const lan = devLanHost();
  if (!isLoopbackHost(hostname) || !lan) return origin;
  return `${protocol}//${lan}${port ? `:${port}` : ""}`;
};

/**
 * The URL the desk encodes into the pairing QR. The session id is a bearer
 * capability and this URL carries it — that is the pairing act itself, and is
 * distinct from gate (b), which concerns the DESK's long-lived socket URL being
 * written into server logs. This URL is displayed on screen and typed by a
 * camera, never logged by the relay.
 */
export const phoneScanUrl = (session: string): string =>
  `${pairingOrigin()}${PHONE_SCAN_PATH}?${SESSION_KEY}=${encodeURIComponent(session)}`;

/** The phone reads its session out of the QR-delivered URL. */
export const sessionFromUrl = (): string | null =>
  new URLSearchParams(window.location.search).get(SESSION_KEY);

// Only the PHONE keeps local state: its per-session token, so a reload silently
// resumes instead of re-prompting for a code. The desk stores nothing — the
// relay owns the shared code and broadcasts it.
export const loadPhoneToken = (session: string): string | null => {
  try {
    return localStorage.getItem(PHONE_TOKEN_PREFIX + session);
  } catch {
    return null;
  }
};
export const savePhoneToken = (session: string, token: string): void => {
  try {
    localStorage.setItem(PHONE_TOKEN_PREFIX + session, token);
  } catch {
    /* private mode — the phone simply re-pairs next load */
  }
};
export const clearPhoneToken = (session: string): void => {
  try {
    localStorage.removeItem(PHONE_TOKEN_PREFIX + session);
  } catch {
    /* ignore */
  }
};

/* ──────────────────────────────── HTTP ops ──────────────────────────────── */

interface PostResult {
  status: number;
  data: Record<string, unknown>;
}

/**
 * `credentials` is the desk/phone split: desk ops (`host`) are cookie-
 * authenticated as of SCAN-3 gate (a); the phone has no session and must not
 * send one (the relay's CORS is credentials:false for exactly that reason).
 */
const post = async (op: string, body: unknown, withCredentials: boolean): Promise<PostResult> => {
  try {
    const res = await fetch(`/scan/${op}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      ...(withCredentials ? { credentials: "include" as const } : {}),
      body: JSON.stringify(body),
    });
    let data: Record<string, unknown> = {};
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON body — status still decides */
    }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: {} };
  }
};

/** DESK: register / rotate the expected one-time code. Requires the staff session. */
export const registerHost = async (session: string, code: string): Promise<boolean> => {
  const r = await post("host", { s: session, code }, true);
  return r.status === 200 && r.data.ok === true;
};

/** PHONE: submit the code → its own token. Sends the UA so the desk can label the device. */
export const pairPhone = async (session: string, code: string): Promise<string | null> => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const r = await post("pair", { s: session, code, ua }, false);
  return r.data.ok === true && typeof r.data.token === "string" ? r.data.token : null;
};

/** PHONE: is a stored token still live? */
export const resumePhone = async (session: string, token: string): Promise<boolean> => {
  const r = await post("resume", { s: session, token }, false);
  return r.data.ok === true;
};

/** PHONE: remove itself. */
export const unpairSelf = async (session: string, token: string): Promise<void> => {
  await post("unpair", { s: session, token }, false);
};

/** DESK: remove ONE paired device by id. */
export const unpairDevice = async (session: string, id: string): Promise<void> => {
  await post("unpair", { s: session, id }, true);
};

/** PHONE → desk: publish a decoded scan with the live token. */
export const sendScan = async (
  session: string,
  token: string,
  ev: Pick<ScanEvent, "kind" | "value" | "raw" | "format">,
): Promise<SendResult> => {
  const r = await post("send", { s: session, token, ...ev }, false);
  if (r.status === 403) return "unpaired";
  if (r.status === 200 && r.data.ok === true) return r.data.delivered ? "ok" : "offline";
  return "error";
};

/* ──────────────────────────────── desk socket ──────────────────────────────── */

export interface HostHandlers {
  /** Roster changed. `needsCode` ⇒ mint + register a fresh code; else adopt `code`. */
  onDevices?: (devices: DeviceInfo[], needsCode: boolean, code: string | null) => void;
  onScan: (ev: ScanEvent) => void;
  onConnected?: (connected: boolean) => void;
}

const RECONNECT_MS = 2000;

/**
 * DESK ← relay. Subscribes over a WebSocket and auto-reconnects until disposed.
 *
 * The session id is offered as the SECOND subprotocol (`erp-scan-v1, <session>`)
 * rather than a `?s=` query — gate (b). The browser sends both as
 * `Sec-WebSocket-Protocol`; the server echoes `erp-scan-v1` back, and the
 * handshake fails if it does not, so an old server is a clean failure rather
 * than a silent downgrade.
 *
 * Auth rides the session cookie: a WebSocket handshake is a normal same-origin
 * GET, so the desk's cookie goes with it and the gateway's guard sees it.
 */
export function subscribeHost(session: string, h: HostHandlers): () => void {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;

  const schedule = (): void => {
    if (closed || retry) return;
    retry = setTimeout(() => {
      retry = null;
      connect();
    }, RECONNECT_MS);
  };

  const connect = (): void => {
    if (closed) return;
    const url = `${window.location.origin.replace(/^http/, "ws")}/scan/events`;
    try {
      ws = new WebSocket(url, [SCAN_WS_PROTOCOL, session]);
    } catch {
      schedule();
      return;
    }
    ws.onopen = () => h.onConnected?.(true);
    ws.onerror = () => {
      /* onclose fires next → reconnect */
    };
    ws.onclose = () => {
      h.onConnected?.(false);
      schedule();
    };
    ws.onmessage = (e: MessageEvent) => {
      let m: Record<string, unknown>;
      try {
        m = JSON.parse(typeof e.data === "string" ? e.data : "") as Record<string, unknown>;
      } catch {
        return;
      }
      if (m.t === "devices") {
        h.onDevices?.(
          Array.isArray(m.devices) ? (m.devices as DeviceInfo[]) : [],
          m.needsCode === true,
          typeof m.code === "string" ? m.code : null,
        );
      } else if (m.t === "scan") {
        h.onScan({
          kind: m.kind as ScanKind,
          value: String(m.value ?? ""),
          raw: String(m.raw ?? m.value ?? ""),
          format: typeof m.format === "string" ? m.format : undefined,
          t: typeof m.ts === "number" ? m.ts : Date.now(),
        });
      }
    };
  };

  connect();
  return () => {
    closed = true;
    if (retry) {
      clearTimeout(retry);
      retry = null;
    }
    try {
      ws?.close();
    } catch {
      /* ignore */
    }
    ws = null;
  };
}
